import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, Filter, ChevronDown, ChevronRight,
  Loader2, Search, X, Sparkles, ZoomIn, Package,
} from 'lucide-react';
import { Product } from '../../types';
import { fetchProducts, fetchBraceCatalogueProducts } from '../../services/products.service';
import { usePicks } from '../../context/PicksContext';
import { staggerContainer, fadeUp } from '../../animations/variants';
import { SaveToPicksButton } from '../picks/SaveToPicksButton';
import { BraceDetailModal } from '../products/BraceDetailModal';

const BRACE_CATEGORIES = [
  { value: 'all', label: 'All Braces', icon: '🔵' },
  { value: 'back', label: 'Back', icon: '🧍' },
  { value: 'shoulder', label: 'Shoulder', icon: '🙆' },
  { value: 'knee', label: 'Knee', icon: '🦵' },
  { value: 'ankle', label: 'Ankle', icon: '🦶' },
  { value: 'wrist', label: 'Wrist', icon: '✋' },
  { value: 'elbow', label: 'Elbow', icon: '💪' },
];

const BRACE_HIGHLIGHTS = [
  { icon: '🧍', name: 'Back Braces', cat: 'back', desc: 'Lumbar & posture support' },
  { icon: '🙆', name: 'Shoulder Braces', cat: 'shoulder', desc: 'Immobilizers & support' },
  { icon: '🦵', name: 'Knee Braces', cat: 'knee', desc: 'Stability & pain relief' },
  { icon: '🦶', name: 'Ankle Braces', cat: 'ankle', desc: 'Sprain & stability support' },
  { icon: '✋', name: 'Wrist Braces', cat: 'wrist', desc: 'Carpal tunnel & sprains' },
  { icon: '💪', name: 'Elbow Braces', cat: 'elbow', desc: 'Tennis & golfer\'s elbow' },
];

const BODY_AREA_ICONS: Record<string, string> = {
  back: '🧍',
  shoulder: '🙆',
  knee: '🦵',
  ankle: '🦶',
  wrist: '✋',
  elbow: '💪',
};

const BODY_AREA_COLORS: Record<string, { from: string; to: string }> = {
  back: { from: 'from-blue-50', to: 'to-indigo-50' },
  shoulder: { from: 'from-cyan-50', to: 'to-sky-50' },
  knee: { from: 'from-emerald-50', to: 'to-teal-50' },
  ankle: { from: 'from-amber-50', to: 'to-orange-50' },
  wrist: { from: 'from-violet-50', to: 'to-purple-50' },
  elbow: { from: 'from-rose-50', to: 'to-pink-50' },
};

const INITIAL_VISIBLE = 6;
const LOAD_MORE_COUNT = 6;

// ── Brace Card (compact; all configuration happens in BraceDetailModal) ─────
function BraceCard({ product, onOpenDetail }: {
  product: Product;
  onOpenDetail: (product: Product) => void;
}) {
  const areaIcon = BODY_AREA_ICONS[product.subcategory] || '🩹';
  const areaColors = BODY_AREA_COLORS[product.subcategory] || { from: 'from-gray-50', to: 'to-slate-50' };

  const uniqueColors = useMemo(() => {
    const seen = new Map<string, string>();
    for (const v of product.variants) {
      if (v.color && v.colorHex && !seen.has(v.color)) seen.set(v.color, v.colorHex);
    }
    return Array.from(seen.entries());
  }, [product.variants]);

  const sizeCount = useMemo(
    () => new Set(product.variants.map((v) => v.size).filter(Boolean)).size,
    [product.variants]
  );

  const galleryCount = (product.images || []).filter((src) => !/size-chart/.test(src)).length;

  return (
    <motion.div
      variants={fadeUp}
      className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg border border-gray-100 transition-shadow flex flex-col cursor-pointer"
      onClick={() => onOpenDetail(product)}
    >
      {/* Image */}
      <div className={`relative h-48 overflow-hidden group ${product.imageUrl ? 'bg-white' : `bg-gradient-to-br ${areaColors.from} ${areaColors.to} flex items-center justify-center`}`}>
        {product.imageUrl ? (
          <>
            <img
              src={product.imageUrl}
              alt={product.name}
              className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2 shadow-md">
                <ZoomIn size={18} className="text-ndms-navy" />
              </div>
            </div>
            {galleryCount > 1 && (
              <span className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                1/{galleryCount}
              </span>
            )}
          </>
        ) : (
          <div className="text-6xl select-none">{areaIcon}</div>
        )}

        {/* Brand + Most Popular (stacked top-left) */}
        <div className="absolute top-2 left-2 flex flex-col items-start gap-1">
          <span className="bg-white/95 text-ndms-navy text-[10px] font-bold px-2.5 py-1 rounded-full shadow-sm uppercase tracking-wider">
            {product.brand}
          </span>
          {product.popular && (
            <div className="flex items-center gap-1 bg-gradient-to-r from-[#7B68CC] to-[#a594e6] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-md">
              <Sparkles size={11} className="fill-white" />
              Most Popular
            </div>
          )}
        </div>

        {/* Quick-save heart (top-right — stock badge moved off the card) */}
        <div className="absolute top-2 right-2">
          <SaveToPicksButton
            product={product}
            variant={null}
            selections={{}}
            size="sm"
            lineName={product.name}
            stopPropagation
            className="bg-white/90 backdrop-blur shadow-sm hover:shadow-md"
          />
        </div>
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        {product.series && (
          <p className="text-[10px] font-semibold text-gray-400 tracking-wide mb-0.5">Series {product.series}</p>
        )}
        <h3 className="font-bold text-gray-900 text-sm leading-snug mb-1">{product.name}</h3>
        <p
          className="text-xs text-gray-500 leading-relaxed mb-3"
          style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
        >
          {product.description}
        </p>

        <div className="mt-auto flex items-center justify-between gap-2">
          <p className="text-[11px] text-gray-500">
            <span className="capitalize font-semibold text-gray-600">{product.subcategory}</span>
            {sizeCount > 0 && <> · {sizeCount} size{sizeCount !== 1 ? 's' : ''}</>}
          </p>
          {uniqueColors.length > 0 && (
            <div className="flex items-center gap-1">
              {uniqueColors.map(([color, hex]) => (
                <span
                  key={color}
                  title={color}
                  className="w-4 h-4 rounded-full border-2 border-white shadow-sm"
                  style={{ backgroundColor: hex }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ── Main Section ─────────────────────────────────────────────────────────────
export function BracesSection() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [catalogueLoading, setCatalogueLoading] = useState(false);
  const [catalogueLoaded, setCatalogueLoaded] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [popularOnly, setPopularOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const [searchQuery, setSearchQuery] = useState('');

  // Detail modal state
  const [detailProduct, setDetailProduct] = useState<Product | null>(null);
  const [detailPrefill, setDetailPrefill] = useState<{ color?: string; size?: string } | undefined>(undefined);

  const openDetail = useCallback((product: Product) => {
    setDetailPrefill(undefined);
    setDetailProduct(product);
  }, []);

  const closeDetail = useCallback(() => {
    setDetailProduct(null);
    setDetailPrefill(undefined);
  }, []);

  // Watch pending-config requests for braces products — open the modal prefilled
  const { state: picksState, clearPendingConfig, closePicks, removePick } = usePicks();
  useEffect(() => {
    const pc = picksState.pendingConfig;
    if (!pc) return;
    if (pc.product.category !== 'braces') return;
    const loaded = products.find((p) => p.productId === pc.product.productId) || pc.product;
    setDetailPrefill({ color: pc.prefill.color, size: pc.prefill.size });
    setDetailProduct(loaded);
    closePicks();
    if (pc.fromPickId) removePick(pc.fromPickId);
    clearPendingConfig();
  }, [picksState.pendingConfig, products, clearPendingConfig, closePicks, removePick]);

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE);
  }, [selectedBrand, selectedCategory, searchQuery, popularOnly]);

  const loadCatalogue = useCallback(() => {
    setCatalogueLoading(true);
    fetchBraceCatalogueProducts()
      .then((catalogueProducts) => {
        if (catalogueProducts.length > 0) {
          setProducts((prev) => {
            const existingIds = new Set(prev.map((p) => p.productId));
            const fresh = catalogueProducts.filter((p) => !existingIds.has(p.productId));
            return fresh.length > 0 ? [...prev, ...fresh] : prev.length > 0 ? prev : catalogueProducts;
          });
        }
        setCatalogueLoading(false);
        setCatalogueLoaded(true);
      })
      .catch(() => {
        setCatalogueLoading(false);
        setCatalogueLoaded(true);
      });
  }, []);

  const loadProducts = useCallback(() => {
    setLoading(true);
    setError(false);
    fetchProducts('braces')
      .then((data) => {
        setProducts(data);
        setLoading(false);
        loadCatalogue();
      })
      .catch(() => {
        setLoading(false);
        // Try catalogue directly even if mock/API fails
        loadCatalogue();
        setError(false);
      });
  }, [loadCatalogue]);

  useEffect(() => {
    loadProducts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive brands dynamically from loaded products
  const derivedBrands = useMemo(() => {
    const seen = new Map<string, string>();
    for (const p of products) {
      if (!seen.has(p.brandSlug)) seen.set(p.brandSlug, p.brand);
    }
    return Array.from(seen.entries()).map(([slug, name]) => ({ slug, name }));
  }, [products]);

  const filtered = useMemo(() => {
    const list = products.filter((p) => {
      if (selectedBrand !== 'all' && p.brandSlug !== selectedBrand) return false;
      if (selectedCategory !== 'all' && p.subcategory !== selectedCategory) return false;
      if (popularOnly && !(p as any).popular) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        const match = p.name.toLowerCase().includes(q) ||
          (p.series || '').toLowerCase().includes(q) ||
          p.productId.toLowerCase().includes(q) ||
          (p.description || '').toLowerCase().includes(q);
        if (!match) return false;
      }
      return true;
    });
    // Stable sort: popular products to the top, original order otherwise
    return list
      .map((p, i) => ({ p, i }))
      .sort((a, b) => {
        const ap = (a.p as any).popular ? 0 : 1;
        const bp = (b.p as any).popular ? 0 : 1;
        if (ap !== bp) return ap - bp;
        return a.i - b.i;
      })
      .map(({ p }) => p);
  }, [products, selectedBrand, selectedCategory, popularOnly, searchQuery]);

  const visibleProducts = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;
  const remaining = filtered.length - visibleCount;

  // Per-body-area product counts for the highlight tiles (derived, not hardcoded)
  const areaCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of products) {
      counts[p.subcategory] = (counts[p.subcategory] || 0) + 1;
    }
    return counts;
  }, [products]);

  return (
    <section id="braces" className="py-20 sm:py-28 bg-ndms-cream">
      <div className="container-max section-padding">
        {/* Header */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          className="text-center mb-12"
        >
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-ndms-navy/5 rounded-full px-4 py-1.5 mb-4">
            <Shield size={16} className="text-ndms-blue" />
            <span className="text-sm font-semibold text-ndms-navy">Orthopedic Support</span>
          </motion.div>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-5xl font-display font-bold text-ndms-navy mb-4">
            Braces & Supports
          </motion.h2>
          <motion.p variants={fadeUp} className="text-gray-600 max-w-2xl mx-auto text-lg leading-relaxed">
            Medical-grade orthopedic braces for back, knee, ankle, wrist, and elbow support. More categories coming soon.
          </motion.p>
        </motion.div>

        {/* Category highlights */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-10"
        >
          {BRACE_HIGHLIGHTS.map(({ icon, name, cat, desc }) => {
            const count = areaCounts[cat] || 0;
            return (
              <motion.button
                key={name}
                variants={fadeUp}
                onClick={() => count > 0 && setSelectedCategory(cat)}
                whileHover={count > 0 ? { y: -3 } : {}}
                whileTap={count > 0 ? { scale: 0.97 } : {}}
                className={`rounded-2xl p-4 text-center shadow-card transition-all border-2 ${
                  count === 0
                    ? 'border-transparent bg-white/50 opacity-50 cursor-default'
                    : selectedCategory === cat
                    ? 'border-ndms-navy bg-ndms-navy/5 cursor-pointer hover:shadow-card-hover'
                    : 'border-transparent bg-white hover:border-ndms-navy/20 cursor-pointer hover:shadow-card-hover'
                }`}
              >
                <div className="text-3xl mb-2">{icon}</div>
                <div className="font-semibold text-ndms-navy text-sm">{name}</div>
                <div className="text-xs text-gray-400 mt-0.5">{desc}</div>
                <div className="mt-2 flex items-center justify-center gap-1 text-xs text-ndms-blue font-medium">
                  {count > 0 ? <>{count} product{count !== 1 ? 's' : ''} <ChevronRight size={10} /></> : <span className="text-gray-300">Coming soon</span>}
                </div>
              </motion.button>
            );
          })}
        </motion.div>

        {/* Search + Filters */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-8"
        >
          {/* Mobile: heading + search + filter toggle */}
          <div className="flex items-center gap-3 mb-3 md:hidden">
            <h3 className="font-semibold text-ndms-navy text-sm">Filters</h3>
            <div className="relative w-44">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search (e.g. 2543)"
                className="w-full pl-7 pr-6 py-1.5 rounded-full border border-gray-200 text-[16px] focus:outline-none focus:border-ndms-navy focus:ring-1 focus:ring-ndms-navy/20 transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={11} />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowFilters((v) => !v)}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-ndms-navy transition-colors ml-auto"
            >
              <Filter size={14} />
              Filters
              <ChevronDown size={14} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>
          <div className={`space-y-3 ${showFilters ? 'block' : 'hidden'} md:block`}>
            {/* Category tabs */}
            <div className="flex flex-wrap gap-2">
              {BRACE_CATEGORIES.map(({ value, label, icon }) => (
                <button
                  key={value}
                  onClick={() => setSelectedCategory(value)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all border-2 ${
                    selectedCategory === value
                      ? 'bg-ndms-navy text-white border-ndms-navy'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-ndms-navy'
                  }`}
                >
                  <span>{icon}</span>
                  {label}
                </button>
              ))}
            </div>

            {/* Brand tabs + desktop inline search */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setSelectedBrand('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all border-2 ${
                  selectedBrand === 'all'
                    ? 'bg-ndms-blue text-white border-ndms-blue'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-ndms-blue'
                }`}
              >
                All Brands
              </button>
              {derivedBrands.map((brand) => (
                <button
                  key={brand.slug}
                  onClick={() => setSelectedBrand(brand.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all border-2 ${
                    selectedBrand === brand.slug
                      ? 'bg-ndms-blue text-white border-ndms-blue'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-ndms-blue'
                  }`}
                >
                  {brand.name}
                </button>
              ))}
              {/* Most Popular toggle */}
              <span className="hidden md:block w-px h-6 bg-gray-200 mx-1" />
              <button
                onClick={() => setPopularOnly((v) => !v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all border-2 ${
                  popularOnly
                    ? 'bg-gradient-to-r from-[#7B68CC] to-[#a594e6] text-white border-[#7B68CC] shadow-sm'
                    : 'bg-white text-[#7B68CC] border-[#7B68CC]/30 hover:border-[#7B68CC]'
                }`}
              >
                <Sparkles size={12} className={popularOnly ? 'fill-white' : ''} />
                Most Popular
              </button>
              {/* Desktop inline search */}
              <div className="relative w-36 hidden md:block">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  className="w-full pl-7 pr-6 py-1.5 rounded-full border border-gray-200 text-xs focus:outline-none focus:border-ndms-navy focus:ring-1 focus:ring-ndms-navy/20 transition-all"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={11} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Product display */}
        {error ? (
          <div className="text-center py-16">
            <div className="text-4xl mb-4">{'\u23F1'}</div>
            <p className="text-gray-600 mb-2 font-medium">Taking a little longer than usual...</p>
            <p className="text-sm text-gray-400 mb-6">Our server is warming up. Please try again.</p>
            <button
              onClick={loadProducts}
              className="px-6 py-2.5 bg-ndms-navy text-white rounded-full text-sm font-semibold hover:bg-ndms-blue transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : loading || (!catalogueLoaded && catalogueLoading) ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 size={32} className="animate-spin text-ndms-blue" />
            <p className="text-gray-400 text-sm">Loading braces...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500 font-medium mb-2">No products found</p>
            <p className="text-gray-400 text-sm mb-4">Try adjusting your filters</p>
            <button
              onClick={() => { setSelectedBrand('all'); setSelectedCategory('all'); setSearchQuery(''); }}
              className="text-ndms-blue text-sm font-medium hover:underline"
            >
              Clear all filters
            </button>
          </div>
        ) : (
          <>
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
            >
              {visibleProducts.map((product) => (
                <BraceCard
                  key={product.productId}
                  product={product}
                  onOpenDetail={openDetail}
                />
              ))}
            </motion.div>

            {/* Show More / Show Less */}
            {filtered.length > INITIAL_VISIBLE && (
              <div className="flex items-center justify-center gap-3 mt-8">
                {hasMore ? (
                  <motion.button
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => setVisibleCount((c) => c + LOAD_MORE_COUNT)}
                    className="px-8 py-3 bg-ndms-navy text-white rounded-full text-sm font-semibold hover:bg-ndms-blue transition-colors shadow-md hover:shadow-lg"
                  >
                    Show More ({remaining} more)
                  </motion.button>
                ) : (
                  <motion.button
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => setVisibleCount(INITIAL_VISIBLE)}
                    className="px-8 py-3 bg-white text-gray-600 rounded-full text-sm font-semibold hover:bg-gray-100 transition-colors border border-gray-200"
                  >
                    Show Less
                  </motion.button>
                )}
              </div>
            )}

            {/* Product count */}
            <p className="text-center text-xs text-gray-400 mt-4">
              Showing {visibleProducts.length} of {filtered.length} brace{filtered.length !== 1 ? 's' : ''}
            </p>
          </>
        )}
      </div>

      {/* Product detail modal */}
      <AnimatePresence>
        {detailProduct && (
          <BraceDetailModal
            product={detailProduct}
            prefill={detailPrefill}
            onClose={closeDetail}
          />
        )}
      </AnimatePresence>
    </section>
  );
}
